import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { findParentByClass, PixelPlaceholderLoader } from "@similarweb/ui-components";

import {
    TOGGLE_OFF_COMPARE_DROPDOWN,
    TOGGLE_ON_COMPARE_DROPDOWN,
} from "app/action_types/website_analysis_action-types";
import { swSettings } from "app/common/services/swSettings";
import type {
    IKeywordsWithGroupsQueryBarProps,
    IKeywordsWithoutGroupsQueryBarProps,
} from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { i18nFilter } from "app/filters/i18nFilter";
import { topicsService } from "pro-features/pages/module start page/src/keyword analysis/topics/utils/flattenTopicsData";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { QueryBarItem } from "../QueryBarItem";

import { KeywordsQueryBarItem } from "./components/KeywordsQueryBarItem";
import { KeywordsQueryBarAutocompleteStyled } from "./EmptyKeywordsQueryBar";
import { findSelectedKeywordGroup } from "./KeywordsQueryBarHelper";
import { KeywordLoaderContainer, QueryBarContainer } from "./KeywordsQueryBarStyles";
import { allTrackers } from "app/services/track/track";

interface Params {
    keyword: string;
    isTerm: string;
    isTopic: string;
}

interface StateParams {
    params: Params;
    state: IRouterState;
}

interface Params {
    keyword: string;
    isTerm: string;
    isTopic: string;
}

interface StateParams {
    params: Params;
    state: IRouterState;
}

interface Params {
    keyword: string;
    isTerm: string;
    isTopic: string;
}

interface StateParams {
    params: Params;
    state: IRouterState;
}

export const KeywordsQueryBar: React.FunctionComponent<
    IKeywordsWithoutGroupsQueryBarProps | IKeywordsWithGroupsQueryBarProps
> = (props) => {
    const {
        isLoading,
        mode,
        keywordGroups,
        selectedKeywordGroupId,
        selectedKeyword,
        onSearchItemClick = null,
        showButtons = true,
        showKeywordGroups = true,
        preventCloseOnEmptyValue = false,
        secondaryText,
        showKeywords,
        showSearch = true,
        showAutoCreateListCTA = true,
        showCompactCreateListCTAButton = true,
        showCompareButton = false,
        onCompareButtonClick,
        showGoToMonitorKeywordListButton = false,
        shouldConfirmDeleteKeywordList = false,
        onSaveKeywordList = (keywordGroup?: IUserKeywordGroup) => undefined,
        onEditKeywordList = (keywordGroup?: IUserKeywordGroup) => undefined,
    } = props;
    const decodedKeyword = decodeURIComponent(selectedKeyword);
    const dispatch = useDispatch();
    const [isGroupShareModalOpen, setIsGroupShareModalOpen] = useState(false);
    const { params }: StateParams = useCurrentStateAndParams();
    const isTerm = params.isTerm === "true";
    const isTopic = params.isTopic === "true";
    const selectedKeywordGroup = findSelectedKeywordGroup(keywordGroups, selectedKeywordGroupId);
    const { isCompareButtonOpen } = useSelector((state) => state.QueryBarReducer);
    const [topicName, setTopicName] = useState<string>("");

    const services = useMemo(() => ({ translate: i18nFilter() }), []);

    useEffect(
        () => () => {
            dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
        },
        [dispatch],
    );

    useEffect(() => {
        if (isTopic) {
            topicsService
                .getTopicById(decodedKeyword)
                .then((res) => setTopicName(res?.Name))
                .catch(() => setTopicName(""));
        }
    }, [isTopic, decodedKeyword]);

    /**
     * Makes sure that we close the search upon clicking outside of the search area
     */
    const handleBodyClick = useCallback(
        (e) => {
            // In case the search group share modal is opened - we don't want to close
            // the search component, since the share modal is rendered from within it
            if (isGroupShareModalOpen) {
                return;
            }

            if (
                !findParentByClass(e.target, "QueryBarAutoComplete") &&
                !e.target.classList.contains("keywordsGroupEditorModal") &&
                !findParentByClass(e.target, "keywordsGroupEditorModal") &&
                !findParentByClass(e.target, "keyword-suggestions") &&
                isCompareButtonOpen
            ) {
                dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
            }
        },
        [isGroupShareModalOpen, isCompareButtonOpen],
    );

    const handleSearchItemClick = useCallback((item) => {
        if (preventCloseOnEmptyValue && item === "") {
            return;
        }

        dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
        if (onSearchItemClick) {
            onSearchItemClick(item);
        } else {
            const currPageState = proNavigator.current();
            const currPageDefaultParams = swSettings.getCurrentComponent().defaultParams;
            if (typeof item === "string") {
                proNavigator.go(currPageState, {
                    ...currPageDefaultParams,
                    keywordGroupId: item,
                    keyword: isTerm ? item : `*${item}`,
                    isTerm: isTerm,
                    webSource: "Total",
                    ...(currPageState.defaultQueryParams ?? {}),
                });
            } else {
                proNavigator.go(currPageState, {
                    ...currPageDefaultParams,
                    keywordGroupId: item.Id ?? item.name,
                    keyword: `*${item.Id ?? item.name}`,
                    isTerm: false,
                    webSource: "Total",
                    ...(currPageState.defaultQueryParams ?? {}),
                });
            }
        }
    }, []);

    const handleCompareButtonClick = () => {
        if (onCompareButtonClick) {
            allTrackers.dispatchTrackEvent("Compare", "open", "Header - keyword");
            onCompareButtonClick();
        }
    };

    useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, { capture: true });

        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, [handleBodyClick]);

    if (!keywordGroups?.length && mode !== "keyword" && !isLoading) {
        return null;
    }

    const itemComponent = !isLoading && (
        <KeywordsQueryBarItem
            keyword={decodedKeyword}
            keywordGroup={selectedKeywordGroup}
            mode={mode}
            onItemClick={() => dispatch({ type: TOGGLE_ON_COMPARE_DROPDOWN })}
            secondaryText={secondaryText}
            showSearch={showSearch}
            services={{
                translate: services.translate,
                swNavigator: proNavigator,
            }}
            showAutoCreateListCTA={showAutoCreateListCTA}
            showCompactCreateListCTAButton={showCompactCreateListCTAButton}
            showButtons={showButtons}
            showCompareButton={showCompareButton}
            showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
            shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
            onCompareButtonClick={handleCompareButtonClick}
            onEditKeywordList={onEditKeywordList}
        />
    );

    const { id, value } = (() => {
        switch (mode) {
            case "searchTerm":
            case "aiTopic":
            case "keyword":
                return { id: decodedKeyword, value: decodedKeyword };
            case "keywordGroupFull":
            case "keywordGroup":
                return { id: selectedKeywordGroup?.id, value: selectedKeywordGroup?.text };
            case "topic":
                return { id: topicName, value: topicName };
        }
    })();

    const searchComponent = (
        <KeywordsQueryBarAutocompleteStyled
            $isLargeBar={showKeywordGroups}
            onSaveKeywordList={onSaveKeywordList}
            onItemClick={handleSearchItemClick}
            selectedValue={value}
            mode={mode}
            showKeywords={showKeywords}
            className={"QueryBarAutoComplete"}
            showAutoCreateListCTA={showAutoCreateListCTA}
            showGoToMonitorKeywordListButton={showGoToMonitorKeywordListButton}
            shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
            onGroupShareModalOpened={() => {
                setIsGroupShareModalOpen(true);
            }}
            onGroupShareModalClosed={() => {
                // Upon closing the share modal - we want to close
                // both the modal and the autocomplete (search)
                setIsGroupShareModalOpen(false);
                dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
            }}
            onGroupEditorModalClosed={() => {
                dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
            }}
            onGroupDelete={(deletedGroup: { Id: string }) => {
                if (mode !== "keywordGroup") {
                    return;
                }

                // In case the keyword group we're currently deleting from the autocomplete
                // is the currently selected keyword group, then we should navigate the user back to the homepage
                if (selectedKeywordGroup?.id === deletedGroup?.Id) {
                    const homeState = proNavigator.current().homeState;
                    proNavigator.go(homeState);
                }
            }}
        />
    );

    const loadingComponent = (
        <KeywordLoaderContainer>
            <PixelPlaceholderLoader
                width={140}
                height={26}
            />
        </KeywordLoaderContainer>
    );

    // Logic for enabling search / loader, as used by
    // the QueryBarItem component
    const shouldEnableSearchOrLoad = isCompareButtonOpen || isLoading;
    return (
        <QueryBarContainer>
            <QueryBarItem
                id={id}
                isLoading={isLoading}
                activeItem={shouldEnableSearchOrLoad ? id : null}
                renderComponent={itemComponent}
                searchComponent={searchComponent}
                loadingComponent={loadingComponent}
            />
        </QueryBarContainer>
    );
};
